import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useAddNewCustomerAddress from '@/api/mutations/customers/useAddNewCustomerAddress';
import customerKeys from '@/api/queries/customers/keyFactory';
import {
  AddNewCustomerAddressMutation as _AddNewCustomerAddressMutation,
  AddNewCustomerAddressInput,
  CustomerQuery,
} from '@/gql/graphql';

const useOptimisticAddNewCustomerAddress = (
  customerId: string,
  options?: UseMutationOptions<
    _AddNewCustomerAddressMutation,
    unknown,
    AddNewCustomerAddressInput
  >
) => {
  const queryClient = useQueryClient();

  return useAddNewCustomerAddress({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: customerKeys.customer(customerId),
      });

      // Snapshot the previous value
      const previousCustomer = queryClient.getQueryData(
        customerKeys.customer(customerId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerQuery>(
        customerKeys.customer(customerId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            customer: {
              ...old.customer,
              addresses: [
                ...old.customer.addresses,
                {
                  id: 'temp-id',
                  ...variables.address,
                  contacts:
                    variables.address.contacts?.map((contact) => ({
                      id: 'temp-id',
                      ...contact,
                      // Placeholder until the onSettled refetch returns the real
                      // publicId.
                      publicId: 'temp-id',
                    })) || [],
                },
              ],
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomer };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        customerKeys.customer(customerId),
        // @ts-expect-error
        context.previousCustomer
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
      queryClient.invalidateQueries({
        queryKey: customerKeys.customer(customerId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticAddNewCustomerAddress;
